import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Grab, Kunde, CreateGrabRequest, UpdateGrabRequest, GRAB_STATUS } from '../types';
import grabService from '../services/grabService';
import kundeService from '../services/kundeService';
import KundeSelect from '../components/KundeSelect';
import { useFriedhof } from '../contexts/FriedhofContext';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { STATUS_FILL } from '../components/lageplan/leafletUtils';
import {
  Button, Card, PageHeader, ContextChip, Modal, Field, Input, Select, Textarea, FormActions,
  LoadingScreen, EmptyState, Icon, inputClass, tableCard, tableScroll, th, td, tableRow,
  GrabStein,
  FilterStand, filterAktiv,
} from '../components/ui';

export const grabStatusLabels: Record<string, string> = {
  FREI: 'Frei', BELEGT: 'Belegt', RESERVIERT: 'Reserviert', IN_NUTZUNG: 'In Nutzung'
};
export const grabStatusColors: Record<string, string> = {
  FREI: 'bg-green-100 text-green-800', BELEGT: 'bg-gray-200 text-gray-800',
  RESERVIERT: 'bg-yellow-100 text-yellow-800', IN_NUTZUNG: 'bg-blue-100 text-blue-800'
};

// Filter überleben die Navigation zur Detailseite (bis "Zurücksetzen" oder Friedhofswechsel).
const FILTER_STORAGE_KEY = 'graeber.filter';

interface StoredFilter {
  friedhofId: number;
  search: string;
  abteilung: string;
  status: string;
}

const loadStoredFilter = (friedhofId: number | null): StoredFilter | null => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredFilter;
    return parsed.friedhofId === friedhofId ? parsed : null;
  } catch {
    return null;
  }
};

// Zuletzt geladene Gräber je Friedhof: beim Zurückkehren zur Liste sofort anzeigen und nur im
// Hintergrund aktualisieren, statt einen Lade-Spinner zu zeigen.
let itemsCache: { friedhofId: number; items: Grab[] } | null = null;


const Graeber: React.FC = () => {
  const navigate = useNavigate();
  const { friedhofId, friedhofName } = useFriedhof();
  const [items, setItems] = useState<Grab[]>(() => (itemsCache?.friedhofId === friedhofId ? itemsCache.items : []));
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(itemsCache?.friedhofId !== friedhofId);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Grab | null>(null);
  const [formData, setFormData] = useState<any>({ abteilung: '', grabnummer: '', status: 'FREI', notizen: '', kundeId: 0, active: true });
  const [stored] = useState(() => loadStoredFilter(friedhofId));
  const [search, setSearch] = useState(stored?.search ?? '');
  const [filterAbteilung, setFilterAbteilung] = useState<string>(stored?.abteilung ?? 'ALL');
  const [filterStatus, setFilterStatus] = useState<string>(stored?.status ?? 'ALL');

  // Distinct full abteilung values (e.g. "1A"), naturally sorted, for the filter dropdown.
  const abteilungen = useMemo(
    () => Array.from(new Set(items.map(i => i.abteilung)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [items]
  );

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter(i => {
        if (filterAbteilung !== 'ALL' && i.abteilung !== filterAbteilung) return false;
        if (filterStatus !== 'ALL' && i.status !== filterStatus) return false;
        if (q) {
          const haystack = `${i.abteilung}/${i.grabnummer} ${i.notizen ?? ''}`.toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      // Natural sort: abteilung, then grabnummer numerically (1, 2, … 10, 11 — not 1, 10, 11, 2).
      .sort((a, b) =>
        a.abteilung.localeCompare(b.abteilung, undefined, { numeric: true }) ||
        a.grabnummer.localeCompare(b.grabnummer, undefined, { numeric: true })
      );
  }, [items, search, filterAbteilung, filterStatus]);

  // Kein Blättern: die Liste läuft durch und lädt beim Scrollen still nach. Die Stelle in der Liste
  // stellt beim Zurückkommen die allgemeine Scroll-Wiederherstellung (Layout) wieder her.
  const { sichtbar, Ende } = useStueckweise(filteredItems);

  // On a friedhof switch while the page is open the filters no longer fit (other abteilungen).
  const prevFriedhofId = useRef(friedhofId);
  useEffect(() => {
    if (prevFriedhofId.current !== friedhofId) {
      prevFriedhofId.current = friedhofId;
      setSearch(''); setFilterAbteilung('ALL'); setFilterStatus('ALL');
      setItems([]); setLoading(true); // keine Gräber des vorherigen Friedhofs anzeigen
    }
  }, [friedhofId]);

  // Filter merken, damit sie den Weg zu einem Grab und zurück überstehen.
  useEffect(() => {
    if (!friedhofId) return;
    const data: StoredFilter = { friedhofId, search, abteilung: filterAbteilung, status: filterStatus };
    try { localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(data)); } catch {/* Speichern ist optional */}
  }, [friedhofId, search, filterAbteilung, filterStatus]);

  const resetFilters = () => { setSearch(''); setFilterAbteilung('ALL'); setFilterStatus('ALL'); };
  const hasActiveFilter = search.trim() !== '' || filterAbteilung !== 'ALL' || filterStatus !== 'ALL';

  const load = useCallback(async () => {
    if (!friedhofId) return;
    try {
      const data = await grabService.getByFriedhof(friedhofId);
      itemsCache = { friedhofId, items: data };
      setItems(data);
    }
    catch { toast.error('Gräber konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, [friedhofId]);

  useEffect(() => { load(); }, [load]);

  // Kunden sind friedhofsunabhängig – einmal laden, für die Zuordnung im Formular.
  useEffect(() => {
    kundeService.getAll().then(setKunden).catch(() => {/* Zuordnung optional, Fehler ignorieren */});
  }, []);

  const resetForm = () => { setFormData({ abteilung: '', grabnummer: '', status: 'FREI', notizen: '', kundeId: 0, active: true }); setEditing(null); setShowForm(false); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!friedhofId) return;
    try {
      const data = {
        friedhofId, abteilung: formData.abteilung, grabnummer: formData.grabnummer,
        status: formData.status, notizen: formData.notizen || undefined,
        kundeId: formData.kundeId || undefined, active: formData.active
      };
      if (editing) {
        await grabService.update(editing.id, data as UpdateGrabRequest);
        markiereNeu(`grab-${editing.id}`);
        toast.success('Grab gespeichert');
      } else {
        const neu = await grabService.create(data as CreateGrabRequest);
        markiereNeu(`grab-${neu.id}`);
        toast.success('Grab angelegt');
      }
      resetForm(); await load();
    } catch (error: any) { toast.error(error?.response?.data?.error || 'Grab konnte nicht gespeichert werden'); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Gräber"
        chip={friedhofName ? <ContextChip>{friedhofName}</ContextChip> : undefined}
        actions={<Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Grab anlegen</Button>}
      />

      {/* Filter & search toolbar */}
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <div className="relative min-w-[220px] flex-1">
          <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${search.trim() ? 'text-brand-600' : 'text-gray-400'}`}><Icon.Search className="h-4 w-4" /></span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Abteilung, Grabnummer oder Notiz…"
            className={`${inputClass} pl-9 ${search.trim() ? filterAktiv : ''}`}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Abteilung:
          <Select value={filterAbteilung} onChange={e => setFilterAbteilung(e.target.value)} className={`w-auto ${filterAbteilung !== 'ALL' ? filterAktiv : ''}`}>
            <option value="ALL">Alle</option>
            {abteilungen.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Status:
          <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`w-auto ${filterStatus !== 'ALL' ? filterAktiv : ''}`}>
            <option value="ALL">Alle</option>
            {GRAB_STATUS.map(s => <option key={s} value={s}>{grabStatusLabels[s]}</option>)}
          </Select>
        </label>
        <FilterStand treffer={filteredItems.length} gesamt={items.length} einheit="Gräber" aktiv={hasActiveFilter} onZuruecksetzen={resetFilters} />
      </Card>

      {showForm && (
        <Modal title={`${editing ? 'Grab bearbeiten' : 'Neues Grab'} · ${friedhofName}`} onClose={resetForm}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Abteilung" required><Input required value={formData.abteilung} onChange={e => setFormData({ ...formData, abteilung: e.target.value })} /></Field>
              <Field label="Grabnummer" required><Input required value={formData.grabnummer} onChange={e => setFormData({ ...formData, grabnummer: e.target.value })} /></Field>
            </div>
            <Field label="Status" required>
              <Select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                {GRAB_STATUS.map(s => <option key={s} value={s}>{grabStatusLabels[s]}</option>)}
              </Select>
            </Field>
            <Field label="Kunde" hint="Optional – kann auch ohne Vertrag zugeordnet werden">
              <KundeSelect value={formData.kundeId || undefined} kunden={kunden} onChange={id => setFormData({ ...formData, kundeId: id ?? 0 })} placeholder="Kunde suchen…" />
            </Field>
            <Field label="Notizen"><Textarea rows={2} value={formData.notizen} onChange={e => setFormData({ ...formData, notizen: e.target.value })} /></Field>
            {editing && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                Aktiv
              </label>
            )}
            <FormActions>
              <Button type="button" variant="secondary" onClick={resetForm}>Abbrechen</Button>
              <Button type="submit">{editing ? 'Speichern' : 'Anlegen'}</Button>
            </FormActions>
          </form>
        </Modal>
      )}

      <div className={tableCard}>
        <div className={tableScroll}>
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-1/3" />
              <col />
            </colgroup>
            <thead className="bg-gray-50/80">
              <tr>
                <th className={`${th} !px-5`}>Grab</th>
                <th className={`${th} !px-5`}>Status</th>
                <th className={`${th} !px-5`}>Tiere</th>
                <th className={`${th} !px-5`}>Kunde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sichtbar.map(item => {
                const a = ankunft(`grab-${item.id}`);
                const weitere = (item.tiere?.length ?? 0) - 1;
                return (
                  <tr
                    key={item.id}
                    ref={a.ref}
                    className={`${tableRow} cursor-pointer ${a.className}`}
                    onClick={() => navigate(`/graeber/${item.id}`)}
                  >
                    <td className={`${td} !px-5 whitespace-nowrap`}>
                      <span className="font-medium tabular-nums text-gray-900">{item.abteilung}/{item.grabnummer}</span>
                    </td>
                    <td className={`${td} !px-5 whitespace-nowrap`}>
                      <span className="inline-flex items-center gap-2 text-gray-700">
                        <GrabStein farbe={STATUS_FILL[item.status] ?? '#9ca3af'} klein />
                        {grabStatusLabels[item.status] || item.status}
                      </span>
                    </td>
                    <td className={`${td} !px-5`}>
                      {item.tierId && (
                        <span className="text-gray-900">
                          {item.tierName || 'Ohne Namen'}
                          {item.tierArt && <span className="text-gray-500">, {item.tierArt}</span>}
                          {weitere > 0 && <span className="text-gray-500"> und {weitere} {weitere === 1 ? 'weiteres' : 'weitere'}</span>}
                        </span>
                      )}
                    </td>
                    <td className={`${td} !px-5 text-gray-700`}>{item.kundeName}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {Ende}
        </div>
        {filteredItems.length === 0 && (
          items.length === 0 ? (
            <EmptyState
              icon={<Icon.MapPin />}
              title="Noch keine Gräber auf diesem Friedhof"
              hint="Gräber werden einzeln angelegt oder vom Admin per GeoJSON-Import im Lageplan übernommen."
              action={<Button onClick={() => setShowForm(true)} icon={<Icon.Plus className="h-4 w-4" />}>Grab anlegen</Button>}
            />
          ) : (
            <EmptyState
              icon={<Icon.MapPin />}
              title="Keine Treffer für diesen Filter"
              action={<Button variant="secondary" onClick={resetFilters}>Filter zurücksetzen</Button>}
            />
          )
        )}

      </div>
    </div>
  );
};

export default Graeber;
